import { ColumnDef } from "@tanstack/react-table";
import { buildActionColumn, buildAvatarDetailsColumn, buildCopyTextColumn } from "@/components/Table";
import { renderEmptyCellState } from "@/components/Table/utils";
import { actionId, type TableRowMenu } from "@/hooks/useTableRowMenu";
import { toFormat } from "@/lib/dateHelpers";
import type { MemberElectionApplicationNode } from "../types";
import { memberElectionClassification } from "../_utils/memberElectionClassification";

export type MemberElectionApplicationColumnOptions = {
  isCompactLayout: boolean;
  setMenu: (menu: TableRowMenu) => void;
  onViewPerson?: (pgaId: string) => void;
  onNavigateToPerson?: (pgaId: string) => void;
};

export function buildMemberElectionApplicationColumns({
  isCompactLayout,
  setMenu,
  onViewPerson,
  onNavigateToPerson
}: MemberElectionApplicationColumnOptions): ColumnDef<MemberElectionApplicationNode, unknown>[] {
  return [
    ...buildAvatarDetailsColumn<MemberElectionApplicationNode>({
      isCompactLayout,
      getTitle: (row) => row.person.displayName,
      getSubtitle: (row) => row.email ?? "",
      getPicture: (row) => row.profilePhotoUrl ?? undefined,
      header: "Name",
      size: 220,
      id: "name",
      sortType: "text"
    }),
    buildCopyTextColumn<MemberElectionApplicationNode>({
      accessorKey: "person.pgaId",
      header: "PGA ID",
      copyFieldLabel: "PGA ID",
      getText: (row) => row.person.pgaId,
      shouldShowCopyButton: (row) => !!row.person.pgaId,
      size: 120
    }),
    {
      id: "class",
      header: "Class",
      accessorFn: (row) => memberElectionClassification(row),
      cell: ({ getValue }) => renderEmptyCellState(getValue()),
      size: 140
    },
    {
      id: "section",
      header: "Section",
      accessorFn: (row) => row.person.primarySection?.name,
      cell: ({ getValue }) => renderEmptyCellState(getValue()),
      size: 160,
      enableSorting: true
    },
    {
      id: "primaryFacility",
      header: "Primary Facility",
      accessorFn: (row) => row.person.primaryFacility?.name,
      cell: ({ getValue }) => renderEmptyCellState(getValue()),
      size: 180
    },
    {
      id: "applicationApproved",
      header: "Application Approved",
      accessorFn: (row) => row.approvedAt,
      cell: ({ row }) => (row.original.approvedAt ? toFormat(row.original.approvedAt, "MMM dd, yyyy") : "--"),
      size: 180,
      enableSorting: true
    },
    {
      id: "electionDate",
      header: "Election Date",
      accessorFn: (row) => row.electionDate?.electionDate ?? null,
      cell: ({ row }) =>
        row.original.electionDate
          ? toFormat(row.original.electionDate.electionDate, "MMM dd, yyyy")
          : renderEmptyCellState(null),
      size: 150,
      enableSorting: true
    },
    buildActionColumn<MemberElectionApplicationNode>({
      getActions: (row) =>
        row.person.pgaId
          ? [
              {
                text: "View Person",
                onClick: () => onNavigateToPerson?.(row.person.pgaId)
              }
            ]
          : [],
      onViewClick: onViewPerson
        ? (row) => {
            if (row.person.pgaId) {
              onViewPerson(row.person.pgaId);
            }
          }
        : undefined,
      showViewButton: (row) => !!row.person.pgaId,
      viewButtonTitle: "View person details",
      actionId,
      setMenu,
      getRowId: (row) => row.id
    })
  ];
}
